import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useFlag } from 'src/hooks/flag';
import {
  FETCH_MERGE_CHECKS,
  UPDATE_PULL_REQUEST_DRAFT_STATUS,
} from 'src/redux/pull-request/actions';
import { updatePullRequest } from 'src/redux/pull-request/actions/update-pull-request';
import { fetchPendingMergeStatus } from 'src/redux/pull-request/merge-reducer';
import { mergeCheckPayloadTransformer } from 'src/redux/pull-request/sagas/utils/merge-check-payload-transformer';
import { getCurrentPullRequestUrlPieces } from 'src/redux/pull-request/selectors';
import urls from 'src/redux/pull-request/urls';
import { PULL_REQUEST_DRAFT_REVIEWERS_FLAG_ID } from 'src/sections/global/components/flags/pull-request-draft-reviewers/pull-request-draft-reviewers';
import { getCurrentUser } from 'src/selectors/user-selectors';
import authRequest, { jsonHeaders } from 'src/utils/fetch';

export const useDraftActions = (): {
  markDraft: () => Promise<void>;
  markReadyForReview: () => Promise<void>;
} => {
  const dispatch = useDispatch();
  const { owner, slug, id } = useSelector(getCurrentPullRequestUrlPieces);
  const { showFlagComponent, showErrorFlag } = useFlag();
  const currentUser = useSelector(getCurrentUser);

  const updateDraftStatus = useCallback(
    async (draft: boolean) => {
      if (!owner || !slug || !id) {
        return;
      }

      const updateUrl = urls.api.v20.pullRequest(owner, slug, id);
      const mergeCheckUrl = urls.api.internal.mergeChecks(owner, slug, id);
      try {
        dispatch({
          type: UPDATE_PULL_REQUEST_DRAFT_STATUS.REQUEST,
        });

        // Make request to update draft status of the current PR
        const updateRequest = authRequest(updateUrl, {
          method: 'PUT',
          headers: jsonHeaders,
          body: JSON.stringify({
            draft,
          }),
        });
        const response = await fetch(updateRequest);
        if (!response.ok) {
          throw Error(response.statusText);
        }

        const pullRequestData = await response.json();

        // Make the request to fetch updated merge info. The
        // important property in this request is the
        // `is_mergable_by_current_user` property which is:
        // - `false` for draft PRs
        // - sometimes `true` for open PRs
        const mergeCheckRequest = authRequest(mergeCheckUrl, {
          method: 'GET',
          headers: jsonHeaders,
        });
        const mergeCheckResponse = await fetch(mergeCheckRequest);
        if (mergeCheckResponse.ok) {
          const mergeCheckData = await mergeCheckResponse.json();
          dispatch({
            type: FETCH_MERGE_CHECKS.SUCCESS,
            payload: mergeCheckPayloadTransformer(mergeCheckData),
          });
        } else {
          // Failing to fetch merge checks should not throw an error
          // if the PR's draft status was successfully updated.
          // We can dispatch this action which will prompt the merge
          // checks card to show an error state.
          dispatch({
            type: FETCH_MERGE_CHECKS.ERROR,
            payload: mergeCheckResponse.statusText,
          });
        }

        /* Updates the current pull request in Redux along
         * with 2 other actions:
         * - Sets `isDraftStatusLoading` to `false`
         * - Adds an entry to the activity feed with the draft update
         */
        dispatch(
          updatePullRequest({
            type: UPDATE_PULL_REQUEST_DRAFT_STATUS.SUCCESS,
            pullRequest: pullRequestData,
            currentUser,
          })
        );

        // Show the draft reviewers flag only when successfully marking as draft
        if (draft) {
          showFlagComponent(PULL_REQUEST_DRAFT_REVIEWERS_FLAG_ID);
        } else {
          // If the PR was marked ready, we should check if the user
          // can create a pending merge.
          dispatch(fetchPendingMergeStatus());
        }
      } catch (e) {
        dispatch({
          type: UPDATE_PULL_REQUEST_DRAFT_STATUS.ERROR,
        });
        showErrorFlag(e.message);
      }
    },
    [owner, slug, id, dispatch, currentUser, showErrorFlag, showFlagComponent]
  );

  const markDraft = useCallback(async () => {
    await updateDraftStatus(true);
  }, [updateDraftStatus]);

  const markReadyForReview = useCallback(async () => {
    await updateDraftStatus(false);
  }, [updateDraftStatus]);

  return {
    markDraft,
    markReadyForReview,
  };
};
